import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  PencilSimple,
  Trash,
  Check,
  ArrowCounterClockwise,
  CalendarBlank,
  Package,
  Drop,
  Buildings,
  MapPin,
  Note,
  Phone,
  EnvelopeSimple,
  CurrencyDollar,
  CreditCard,
  Printer,
  DotsThree,
  PaperPlaneTilt,
  FilePdf,
  Truck,
  UserCircle,
  ChatText,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { AppHeader } from "@/components/AppHeader";
import { GoodsReceiptGrid, GoodsReceiptHistory } from "@/components/GoodsReceipt";
import { POPrintSheet } from "@/components/POPrintSheet";
import { POEmailDialog } from "@/components/POEmailDialog";
import { FileVault } from "@/components/FileVault";
import { PO_BADGE, PO_ACCENT } from "@/pages/PurchaseOrders";
import { fmtMoney, currencyLabel } from "@/lib/poFormat";
import { fmtSqft, lineSqft, lineTotal, computedLineTotal, hasOverride } from "@/lib/poPricing";
import { api, API, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { repText, smsHref } from "@/lib/sms";

// Click the line total to type the supplier's figure; blank resets to qty × price.
const LineTotalCell = ({ line, cur, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const adjusted = hasOverride(line);

  const start = () => {
    setVal(lineTotal(line).toFixed(2));
    setEditing(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await onSave(val.trim() === "" ? null : Number(val));
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  if (editing)
    return (
      <td className="p-2 text-right" data-testid={`po-line-total-${line.line_id}`}>
        <div className="flex items-center justify-end gap-1">
          <Input
            autoFocus
            data-testid={`po-line-total-input-${line.line_id}`}
            type="number"
            step="0.01"
            min="0"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") setEditing(false);
            }}
            className="font-mono-data h-9 w-32 border-zinc-700 bg-zinc-950 text-right"
          />
          <button
            type="button"
            data-testid={`po-line-total-save-${line.line_id}`}
            disabled={busy}
            onClick={save}
            className="rounded-md bg-emerald-500 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-950 hover:bg-emerald-400 disabled:opacity-40"
          >
            Save
          </button>
          {adjusted && (
            <button
              type="button"
              data-testid={`po-line-total-reset-${line.line_id}`}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onSave(null);
                  setEditing(false);
                } finally {
                  setBusy(false);
                }
              }}
              className="rounded-md border border-zinc-700 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800 disabled:opacity-40"
            >
              Reset
            </button>
          )}
        </div>
      </td>
    );

  return (
    <td
      className="font-mono-data p-3 text-right font-semibold text-zinc-100"
      data-testid={`po-line-total-${line.line_id}`}
    >
      <button
        type="button"
        data-testid={`po-line-total-edit-${line.line_id}`}
        onClick={start}
        title="Adjust this line's total to match the supplier's PO"
        className="group inline-flex flex-col items-end rounded-md px-1 hover:bg-zinc-800"
      >
        <span className="inline-flex items-center gap-1">
          {fmtMoney(lineTotal(line), cur)}
          <PencilSimple
            size={12}
            weight="bold"
            className="text-zinc-600 opacity-0 transition-opacity group-hover:opacity-100"
          />
        </span>
        {adjusted && (
          <span
            data-testid={`po-line-total-adjusted-${line.line_id}`}
            className="text-[10px] font-normal text-amber-400"
          >
            adjusted · was {fmtMoney(computedLineTotal(line), cur)}
          </span>
        )}
      </button>
    </td>
  );
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

const InfoRow = ({ icon: Icon, children, testid }) =>
  children ? (
    <div
      className="flex items-start gap-2.5 text-sm text-zinc-300"
      data-testid={testid}
    >
      <Icon size={15} weight="bold" className="mt-0.5 shrink-0 text-zinc-500" />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  ) : null;

const Card = ({ icon: Icon, tone, title, testid, action, children }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-5 backdrop-blur-md transition-colors duration-150 hover:border-zinc-700"
  >
    <div className="mb-3 flex items-center justify-between border-b border-zinc-800 pb-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-zinc-300">
        <Icon size={16} weight="bold" className={tone} />
        {title}
      </div>
      {action}
    </div>
    <div className="space-y-2.5">{children}</div>
  </div>
);

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [slabs, setSlabs] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [company, setCompany] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/purchase-orders/${id}`);
    setPo(data);
    if (data.received_slab_ids?.length) {
      const all = await api.get("/slabs");
      setSlabs(all.data.filter((s) => data.received_slab_ids.includes(s.id)));
    } else {
      setSlabs([]);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/settings/company")
      .then(({ data }) => setCompany(data))
      .catch(() => setCompany(null));
    api
      .get("/settings/branding")
      .then(({ data }) => setHasLogo(!!data?.logo))
      .catch(() => setHasLogo(false));
  }, []);

  useEffect(() => {
    api
      .get("/inventory/locations")
      .then(({ data }) => setLocations(data.locations || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!po?.supplier_id) {
      setSupplier(null);
      return;
    }
    api
      .get(`/suppliers/${po.supplier_id}`)
      .then(({ data }) => setSupplier(data))
      .catch(() => setSupplier(null));
  }, [po?.supplier_id]);

  const startReceive = (line) => {
    setReceiving(line.line_id);
  };

  const receiveSink = async (line) => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${id}/receive`, {
        line_id: line.line_id,
        length_in: 1,
        width_in: 1,
      });
      toast.success("Sink added to sink stock");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not receive sink",
      );
    }
    setBusy(false);
  };

  const receiveAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/purchase-orders/${id}/receive-all`);
      toast.success(
        data.created
          ? `${data.created} slab${data.created === 1 ? "" : "s"} pulled into inventory`
          : "Purchase order marked received",
      );
      setReceiving(null);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not mark received",
      );
    }
    setBusy(false);
  };

  const unreceive = async (slab) => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${id}/unreceive/${slab.id}`);
      toast.success(`${slab.code} pulled back off this PO`);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not un-receive slab",
      );
    }
    setBusy(false);
  };

  const remove = async () => {
    await api.delete(`/purchase-orders/${id}`);
    toast.success("Purchase order deleted");
    navigate("/inventory/purchase-orders");
  };

  if (!po)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Purchase order" />
      </div>
    );

  const cur = po.currency || "USD";
  const itemsSubtotal = po.lines.reduce((n, l) => n + lineTotal(l), 0);
  const charges = po.additional_charges || [];
  const chargesSubtotal = charges.reduce((n, c) => n + (Number(c.amount) || 0), 0);
  const grandTotal = itemsSubtotal + chargesSubtotal;

  const total = po.lines.reduce((n, l) => n + (l.qty || 0), 0);
  const got = po.lines.reduce((n, l) => n + (l.received_qty || 0), 0);
  const pct = total ? Math.round((got / total) * 100) : 0;
  const canReceive = po.status !== "Received" && po.status !== "Cancelled";

  const shipName = company?.name || "Your warehouse";
  const emailLog = [...(po.email_log || [])].reverse();
  const canEmail = po.status !== "Cancelled";

  return (
    <div className="grain min-h-screen">
      <AppHeader title={po.code} />
      <div id="print-area" className="hidden print:block">
        <POPrintSheet
          po={po}
          supplier={supplier}
          company={company}
          logoUrl={hasLogo ? `${API}/public/logo` : ""}
        />
      </div>
      {emailOpen && (
        <POEmailDialog
          po={po}
          supplier={supplier}
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          onSent={setPo}
        />
      )}
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/inventory/purchase-orders"
          className="no-print inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> All purchase orders
        </Link>

        {/* Header */}
        <div className="relative mt-4 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/80 p-5 pl-6 backdrop-blur-md">
          <span
            className={`absolute left-0 top-0 h-full w-1.5 ${PO_ACCENT[po.status] || PO_ACCENT.Draft}`}
          />
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div
                className="font-mono-data text-xs text-indigo-300"
                data-testid="po-header-code"
              >
                {po.code}
              </div>
              <h1
                className="text-2xl font-bold tracking-tight sm:text-3xl"
                data-testid="po-header-title"
              >
                {po.supplier_name || "No supplier"}
              </h1>
              {po.customer_id && (
                <Link
                  to={`/customers/${po.customer_id}`}
                  data-testid="po-customer-link"
                  className="mt-1 inline-flex items-center gap-1.5 text-sm text-indigo-300 hover:underline"
                >
                  For {po.customer_company || po.customer_name}
                </Link>
              )}
              {po.job_id && (
                <Link
                  to={`/projects/${po.job_id}?tab=field`}
                  data-testid="po-job-link"
                  className="mt-1 block text-sm text-indigo-300 hover:underline"
                >
                  Job {po.job_code}
                </Link>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${
                    PO_BADGE[po.status] || PO_BADGE.Draft
                  }`}
                  data-testid="po-status-pill"
                >
                  {po.status}
                </span>
                {po.rush && (
                  <span
                    data-testid="po-rush-badge"
                    className="rounded-full border border-red-500/40 bg-red-500/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-red-300"
                  >
                    🔥 Hot Rush
                  </span>
                )}
                {po.order_date && (
                  <span className="font-mono-data inline-flex items-center gap-1 rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-1 text-[10px] text-zinc-400">
                    <CalendarBlank size={11} weight="bold" /> ordered{" "}
                    {po.order_date}
                  </span>
                )}
                {po.expected_date && (
                  <span className="font-mono-data inline-flex items-center gap-1 rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-1 text-[10px] text-zinc-400">
                    <CalendarBlank size={11} weight="bold" /> expected{" "}
                    {po.expected_date}
                  </span>
                )}
                {po.reference && (
                  <span className="font-mono-data rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-1 text-[10px] text-zinc-500">
                    ref {po.reference}
                  </span>
                )}
                {po.consignment && (
                  <span
                    data-testid="po-consignment-badge"
                    className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-1 text-[10px] uppercase tracking-widest text-amber-400"
                  >
                    Consignment
                  </span>
                )}
                {po.supplier_notified_at && (
                  <span
                    data-testid="po-emailed-badge"
                    title={`Emailed to ${po.supplier_notified_to}`}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[10px] uppercase tracking-widest text-emerald-300"
                  >
                    <PaperPlaneTilt size={11} weight="bold" /> Emailed{" "}
                    {fmtWhen(po.supplier_notified_at)}
                  </span>
                )}
                {po.supplier_confirmation && (
                  <span
                    data-testid="po-confirmed-badge"
                    title={po.supplier_confirmation.note || "Confirmed by the supplier"}
                    className="inline-flex items-center gap-1 rounded-full border border-sky-500/25 bg-sky-500/10 px-2.5 py-1 text-[10px] uppercase tracking-widest text-sky-300"
                  >
                    <Truck size={11} weight="bold" /> Supplier confirmed
                    {po.supplier_confirmation.ship_date
                      ? ` · ships ${po.supplier_confirmation.ship_date}`
                      : ""}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-3">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Order total
                </div>
                <div
                  className="font-mono-data text-3xl font-black tracking-tight text-indigo-400"
                  data-testid="po-grand-total"
                >
                  {fmtMoney(grandTotal, cur)}
                </div>
              </div>
              <div className="no-print flex flex-wrap items-center justify-end gap-2">
                {canEmail && (
                  <Button
                    data-testid="po-email-button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setEmailOpen(true)}
                    className="h-11 border-indigo-500/50 bg-transparent text-xs font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-950/40"
                  >
                    <PaperPlaneTilt size={15} weight="bold" className="mr-2" />
                    {po.supplier_notified_at ? "Re-send to supplier" : "Email supplier"}
                  </Button>
                )}
                {canReceive && (
                  <Button
                    data-testid="mark-received-button"
                    disabled={busy}
                    onClick={receiveAll}
                    className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                  >
                    <Check size={15} weight="bold" className="mr-2" /> Mark
                    Received
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="po-actions-dropdown"
                      className="h-11 border-zinc-800 bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800"
                    >
                      Actions
                      <DotsThree size={18} weight="bold" className="ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="border-zinc-800 bg-zinc-900 text-zinc-200"
                  >
                    <DropdownMenuItem
                      data-testid="edit-po-button"
                      onClick={() =>
                        navigate(`/inventory/purchase-orders/${id}/edit`)
                      }
                      className="cursor-pointer focus:bg-zinc-800"
                    >
                      <PencilSimple size={15} weight="bold" className="mr-2" />{" "}
                      Edit order
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="po-print-button"
                      onClick={() => window.print()}
                      className="cursor-pointer focus:bg-zinc-800"
                    >
                      <Printer size={15} weight="bold" className="mr-2" /> Print
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="po-download-pdf"
                      onClick={() =>
                        downloadFile(`/purchase-orders/${id}/pdf`, `${po.code}.pdf`).catch(() =>
                          toast.error("Could not build the PDF"),
                        )
                      }
                      className="cursor-pointer focus:bg-zinc-800"
                    >
                      <FilePdf size={15} weight="bold" className="mr-2" /> Download PDF
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem
                      data-testid="delete-po-button"
                      onClick={remove}
                      className="cursor-pointer text-red-400 focus:bg-zinc-800 focus:text-red-400"
                    >
                      <Trash size={15} weight="bold" className="mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          <div className="mt-5 max-w-md" data-testid="po-progress">
            <div className="flex items-baseline justify-between">
              <span className="font-mono-data text-sm">
                <span className="text-indigo-300">{got}</span>
                <span className="text-zinc-500"> / {total} received</span>
              </span>
              <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-600">
                {pct}%
              </span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-800">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  pct === 100 ? "bg-emerald-500" : "bg-indigo-500"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          <Card
            icon={Buildings}
            tone="text-indigo-300"
            title="Supplier"
            testid="supplier-info-card"
          >
            <div className="text-sm font-semibold text-zinc-100">
              {po.supplier_name || "No supplier"}
            </div>
            <InfoRow icon={MapPin}>{supplier?.address}</InfoRow>
            <InfoRow icon={Phone}>{supplier?.phone}</InfoRow>
            <InfoRow icon={EnvelopeSimple}>{supplier?.email}</InfoRow>
            <InfoRow icon={CurrencyDollar} testid="po-currency-line">
              {currencyLabel(cur)}
            </InfoRow>
            <InfoRow icon={CreditCard} testid="po-terms-line">
              {po.payment_terms || "No terms set"}
            </InfoRow>
            {(supplier?.rep_name || supplier?.rep_phone) && (
              <div
                className="mt-3 space-y-1.5 border-t border-zinc-800 pt-3"
                data-testid="po-rep-block"
              >
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Sales rep
                </div>
                <InfoRow icon={UserCircle} testid="po-rep-name">
                  {[supplier.rep_name, supplier.rep_title].filter(Boolean).join(" · ")}
                </InfoRow>
                <InfoRow icon={Phone} testid="po-rep-phone">
                  {supplier.rep_phone}
                </InfoRow>
                <InfoRow icon={EnvelopeSimple} testid="po-rep-email">
                  {supplier.rep_email}
                </InfoRow>
                {supplier.rep_phone && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <a
                      href={smsHref(supplier.rep_phone, repText(supplier, company?.name, po))}
                      data-testid="po-rep-text"
                      className="no-print inline-flex h-8 items-center gap-1.5 rounded-md bg-indigo-500 px-2.5 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      <ChatText size={13} weight="bold" /> Text rep
                    </a>
                    <a
                      href={`tel:${supplier.rep_phone.replace(/[^\d+]/g, "")}`}
                      data-testid="po-rep-call"
                      className="no-print inline-flex h-8 items-center gap-1.5 rounded-md border border-zinc-700 px-2.5 text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                    >
                      <Phone size={13} weight="bold" /> Call
                    </a>
                  </div>
                )}
              </div>
            )}
          </Card>

          <Card
            icon={MapPin}
            tone="text-emerald-300"
            title="Ship to"
            testid="shipto-info-card"
          >
            <div className="text-sm font-semibold text-zinc-100">
              {shipName}
            </div>
            <InfoRow icon={MapPin}>{company?.address}</InfoRow>
            <InfoRow icon={Phone}>{company?.phone}</InfoRow>
            <InfoRow icon={EnvelopeSimple}>{company?.email}</InfoRow>
            {!company?.address && !company?.phone && (
              <p className="text-xs text-zinc-500">
                Set your warehouse address in Settings → Company.
              </p>
            )}
          </Card>

          <Card
            icon={Note}
            tone="text-amber-300"
            title="Notes"
            testid="notes-info-card"
          >
            {po.notes ? (
              <p className="whitespace-pre-wrap text-sm text-zinc-300">
                {po.notes}
              </p>
            ) : (
              <p className="text-sm text-zinc-500">No notes on this order.</p>
            )}
          </Card>
        </div>

        {/* Line items */}
        <div className="mt-8 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Purchase order items
          </div>
          <Button
            variant="outline"
            data-testid="po-add-item-button"
            onClick={() => navigate(`/inventory/purchase-orders/${id}/edit`)}
            className="no-print h-9 border-zinc-800 bg-zinc-900 text-[10px] uppercase tracking-widest hover:bg-zinc-800"
          >
            <PencilSimple size={13} weight="bold" className="mr-2" /> Add / edit
            items
          </Button>
        </div>
        <div className="mt-3 overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-900/50">
          <table
            className="w-full min-w-[720px] text-left text-sm"
            data-testid="po-line-items-table"
          >
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="p-3">Product</th>
                <th className="p-3">Attributes</th>
                <th className="p-3 text-right">Qty</th>
                <th className="p-3 text-right">Unit price</th>
                <th className="p-3 text-right">Line total</th>
                <th className="p-3 text-right">Received</th>
                <th className="no-print p-3" />
              </tr>
            </thead>
            <tbody>
              {po.lines.map((l) => {
                const left = (l.qty || 0) - (l.received_qty || 0);
                const isSink = l.kind === "sink";
                const sq = lineSqft(l);
                const attrs = isSink
                  ? [l.sink_type, l.size]
                  : [l.material, l.finish, l.thickness, `${l.length_in}" x ${l.width_in}"`];
                return (
                  <Fragment key={l.line_id}>
                    <tr
                      data-testid="po-line-item-row"
                      className="border-b border-zinc-800/70 align-top last:border-0"
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 text-zinc-400">
                            {isSink ? (
                              <Drop size={15} weight="bold" />
                            ) : (
                              <Package size={15} weight="bold" />
                            )}
                          </span>
                          <span className="font-semibold text-zinc-100">
                            {isSink
                              ? [l.brand, l.model].filter(Boolean).join(" ") ||
                                "Sink"
                              : l.slab_color}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1.5">
                          {attrs.filter(Boolean).map((v) => (
                            <span
                              key={v}
                              className="font-mono-data rounded-full border border-zinc-800 bg-zinc-950 px-2 py-0.5 text-[10px] text-zinc-400"
                            >
                              {v}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="font-mono-data p-3 text-right text-zinc-300">
                        {l.qty}
                        {sq && (
                          <div
                            data-testid={`po-line-sqft-${l.line_id}`}
                            className={`whitespace-nowrap text-[10px] ${
                              sq.basis === "received" ? "text-emerald-400" : "text-zinc-500"
                            }`}
                          >
                            {fmtSqft(sq.sqft)} {sq.basis}
                          </div>
                        )}
                      </td>
                      <td className="font-mono-data p-3 text-right text-zinc-300">
                        {l.unit_cost == null || l.unit_cost === ""
                          ? "—"
                          : fmtMoney(l.unit_cost, cur)}
                        {sq && l.unit_cost != null && l.unit_cost !== "" && (
                          <span className="text-[10px] text-zinc-500">/ft²</span>
                        )}
                      </td>
                      <LineTotalCell
                        line={l}
                        cur={cur}
                        onSave={async (total_override) => {
                          try {
                            const { data } = await api.patch(
                              `/purchase-orders/${po.id}/lines/${l.line_id}`,
                              { total_override },
                            );
                            setPo(data);
                            toast.success(
                              total_override == null ? "Line total reset" : "Line total adjusted",
                            );
                          } catch (err) {
                            toast.error(
                              formatApiErrorDetail(err.response?.data?.detail) ||
                                "Could not adjust the line",
                            );
                            throw err;
                          }
                        }}
                      />
                      <td className="p-3 text-right">
                        <span
                          className={`font-mono-data text-xs ${
                            left <= 0 ? "text-emerald-400" : "text-zinc-400"
                          }`}
                        >
                          {l.received_qty || 0}/{l.qty}
                        </span>
                      </td>
                      <td className="no-print p-3 text-right">
                        <Button
                          data-testid={`receive-line-${l.line_id}`}
                          disabled={left <= 0}
                          onClick={() =>
                            isSink ? receiveSink(l) : startReceive(l)
                          }
                          className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400 disabled:bg-zinc-800 disabled:text-zinc-500"
                        >
                          {left <= 0
                            ? "Complete"
                            : isSink
                              ? "Receive"
                              : "Receive"}
                        </Button>
                      </td>
                    </tr>
                    {receiving === l.line_id && !isSink && (
                      <tr data-testid="receive-panel">
                        <td colSpan={7} className="bg-zinc-950/60 p-4">
                          <GoodsReceiptGrid
                            po={po}
                            line={l}
                            locations={locations}
                            onCancel={() => setReceiving(null)}
                            onDone={() => {
                              setReceiving(null);
                              load();
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-zinc-800 bg-zinc-900/80">
                <td colSpan={4} className="p-3 text-right text-xs uppercase tracking-widest text-zinc-500">
                  Items subtotal
                </td>
                <td
                  className="font-mono-data p-3 text-right font-semibold text-zinc-100"
                  data-testid="po-items-subtotal"
                >
                  {fmtMoney(itemsSubtotal, cur)}
                </td>
                <td colSpan={2} className="no-print" />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Additional charges */}
        <div
          className="mt-8 rounded-xl border border-zinc-800 bg-zinc-900/60 p-5"
          data-testid="po-additional-charges-table"
        >
          <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-300">
              Additional charges
            </div>
            <Button
              variant="outline"
              data-testid="po-add-charge-btn"
              onClick={() => navigate(`/inventory/purchase-orders/${id}/edit`)}
              className="no-print h-9 border-zinc-800 bg-zinc-900 text-[10px] uppercase tracking-widest hover:bg-zinc-800"
            >
              <PencilSimple size={13} weight="bold" className="mr-2" /> Edit
            </Button>
          </div>
          {charges.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-500" data-testid="po-charges-empty">
              No freight, tax or handling charges added.
            </p>
          ) : (
            <div className="mt-3 space-y-2">
              {charges.map((c) => (
                <div
                  key={c.id || c.label}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-zinc-300">{c.label || "Charge"}</span>
                  <span className="font-mono-data text-zinc-100">
                    {fmtMoney(c.amount, cur)}
                  </span>
                </div>
              ))}
              <div
                className="flex items-center justify-between border-t border-zinc-800 pt-3 text-sm font-semibold"
                data-testid="po-additional-charges-subtotal"
              >
                <span className="text-zinc-400">Charges subtotal</span>
                <span className="font-mono-data text-zinc-100">
                  {fmtMoney(chargesSubtotal, cur)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Grand total */}
        <div
          className="mt-6 flex flex-col items-end gap-1 rounded-xl border border-zinc-800 bg-gradient-to-r from-zinc-900 to-indigo-950/30 p-6"
          data-testid="po-grand-total-summary"
        >
          <div className="flex w-full max-w-xs items-center justify-between text-sm text-zinc-400">
            <span>Items</span>
            <span className="font-mono-data">{fmtMoney(itemsSubtotal, cur)}</span>
          </div>
          <div className="flex w-full max-w-xs items-center justify-between text-sm text-zinc-400">
            <span>Charges</span>
            <span className="font-mono-data">
              {fmtMoney(chargesSubtotal, cur)}
            </span>
          </div>
          <div className="mt-2 flex w-full max-w-xs items-center justify-between border-t border-zinc-800 pt-3">
            <span className="text-xs uppercase tracking-widest text-zinc-400">
              Grand total
            </span>
            <span className="font-mono-data text-2xl font-black text-indigo-400">
              {fmtMoney(grandTotal, cur)}
            </span>
          </div>
        </div>

        <GoodsReceiptHistory
          po={po}
          refreshKey={(po.received_slab_ids || []).length}
          locations={locations}
          onChange={load}
        />

        {/* Supplier communication */}
        <div
          className="no-print mt-8 rounded-xl border border-zinc-800 bg-zinc-900/60 p-5"
          data-testid="po-email-history"
        >
          <div className="flex items-center justify-between gap-3 border-b border-zinc-800 pb-3">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-zinc-300">
              <PaperPlaneTilt size={16} weight="bold" className="text-indigo-300" />
              Sent to supplier ({emailLog.length})
            </div>
            {canEmail && (
              <Button
                variant="outline"
                data-testid="po-email-history-send"
                onClick={() => setEmailOpen(true)}
                className="h-9 border-zinc-800 bg-zinc-900 text-[10px] uppercase tracking-widest hover:bg-zinc-800"
              >
                {emailLog.length ? "Send again" : "Email supplier"}
              </Button>
            )}
          </div>
          {po.supplier_confirmation && (
            <div
              className="mt-3 rounded-md border border-sky-500/25 bg-sky-500/5 p-3 text-sm"
              data-testid="po-confirmation"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sky-200">
                  <Truck size={14} weight="bold" className="mr-1.5 inline" />
                  Supplier confirmed
                  {po.supplier_confirmation.contact_name
                    ? ` (${po.supplier_confirmation.contact_name})`
                    : ""}
                  {po.supplier_confirmation.ship_date
                    ? ` — ships ${po.supplier_confirmation.ship_date}`
                    : ""}
                </span>
                <span className="font-mono-data text-xs text-zinc-400">
                  {fmtWhen(po.supplier_confirmation.at)}
                </span>
              </div>
              {po.supplier_confirmation.note && (
                <div className="mt-1 text-xs text-zinc-300">“{po.supplier_confirmation.note}”</div>
              )}
            </div>
          )}
          {po.confirm_token && (
            <button
              type="button"
              data-testid="po-copy-confirm-link"
              onClick={() =>
                navigator.clipboard
                  .writeText(`${window.location.origin}/po/confirm/${po.confirm_token}`)
                  .then(() => toast.success("Confirmation link copied"))
              }
              className="mt-3 text-xs text-zinc-400 underline-offset-2 hover:text-zinc-200 hover:underline"
            >
              Copy the supplier's confirm-ship-date link
            </button>
          )}
          {emailLog.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-500" data-testid="po-email-history-empty">
              Not emailed yet — send the PO and the supplier gets the PDF plus an item list.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-zinc-800/70">
              {emailLog.map((e, i) => (
                <li
                  key={`${e.at}-${i}`}
                  data-testid={`po-email-log-${i}`}
                  className="flex flex-wrap items-start justify-between gap-2 py-2.5 text-sm"
                >
                  <div className="min-w-0">
                    <div className="text-zinc-100">
                      {e.kind === "eta" ? (
                        <span className="mr-1.5 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300">
                          ETA request
                        </span>
                      ) : null}
                      To <span className="font-semibold">{e.to}</span>
                      {e.cc?.length ? (
                        <span className="text-zinc-400"> · cc {e.cc.join(", ")}</span>
                      ) : null}
                    </div>
                    {e.message && (
                      <div className="mt-0.5 truncate text-xs text-zinc-400">“{e.message}”</div>
                    )}
                  </div>
                  <div className="font-mono-data shrink-0 text-right text-xs text-zinc-400">
                    {fmtWhen(e.at)}
                    {e.by && <div className="text-zinc-600">{e.by}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Supplier documents */}
        <div className="mt-8">
          <FileVault
            entity="purchase-orders"
            entityId={id}
            title="Supplier documents — invoices, sales orders, packing lists"
          />
        </div>

        {/* Received into stock */}
        <div
          className="no-print mt-8 rounded-xl border border-zinc-800 bg-zinc-900/60 p-5"
          data-testid="po-received-slabs"
        >
          <div className="flex items-center gap-2">
            <Package size={16} weight="bold" className="text-indigo-300" />
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Received into stock ({slabs.length})
            </div>
          </div>
          {slabs.length === 0 ? (
            <p className="mt-2 text-sm text-zinc-500">Nothing received yet.</p>
          ) : (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {slabs.map((s) => (
                <div
                  key={s.id}
                  className="relative flex items-center justify-between gap-3 overflow-hidden rounded-md border border-zinc-800 bg-zinc-950 p-3 pl-4"
                >
                  <span className="absolute left-0 top-0 h-full w-1 bg-emerald-500" />
                  <Link
                    to={`/inventory/slabs/${s.id}`}
                    data-testid={`po-slab-${s.id}`}
                    className="min-w-0 flex-1 hover:opacity-80"
                  >
                    <span className="font-mono-data block text-xs text-indigo-300">
                      {s.code}
                    </span>
                    <span className="block truncate text-sm font-semibold">
                      {s.slab_color}
                    </span>
                  </Link>
                  <span className="font-mono-data shrink-0 text-xs text-zinc-400">
                    {s.length_in}" x {s.width_in}"
                  </span>
                  <Button
                    data-testid={`unreceive-slab-${s.id}`}
                    variant="outline"
                    disabled={busy}
                    onClick={() => unreceive(s)}
                    className="h-9 shrink-0 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest text-red-400 hover:bg-zinc-800"
                  >
                    <ArrowCounterClockwise
                      size={14}
                      weight="bold"
                      className="mr-1"
                    />{" "}
                    Undo
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
